'use client'
import cn from 'classnames'
import { motion } from 'motion/react'
import Image from 'next/image'
import type { FC } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import type { SliceComponentProps, SliceLike } from '@/lib/rich-text'

/**
 * Bento feature grid. Tiles are either a cropped portal screenshot or one of three
 * CSS mocks (schedule, split layout, proof of play) so the section stays honest
 * about what the portal does without needing a screenshot for every claim.
 */
type Tile = {
  kind: 'image' | 'schedule' | 'layout' | 'proof' | 'displays' | 'plan'
  span?: 2
  eyebrow: string
  title: string
  text: string
  image?: string
  image_alt?: string
  image_class?: string
  badge?: string
}

export interface BentoSlice extends SliceLike<'bento'> {
  variation: 'default'
  primary: {
    eyebrow: string
    title: string
    text: string
    tiles: Tile[]
  }
}

export type BentoProps = SliceComponentProps<BentoSlice>

const Bento: FC<BentoProps> = ({ slice }) => (
  <section
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    className="bg-white py-20 md:py-28"
  >
    <div className="container">
      <div className="max-w-[720px]">
        <p className="eyebrow">{slice.primary.eyebrow}</p>
        <h2 className="display-title mt-3">{slice.primary.title}</h2>
        <p className="mt-5 text-[19px] leading-8 text-[#4b4b4b]">{slice.primary.text}</p>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {slice.primary.tiles.map((tile, i) => (
          <motion.article
            key={tile.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.08, ease: 'easeOut' }}
            className={cn(
              'group relative flex flex-col overflow-hidden rounded-[28px] bg-[#f6f3f8] ring-1 ring-black/5',
              tile.span === 2 && 'md:col-span-2'
            )}
          >
            <div
              className={cn(
                'relative overflow-hidden',
                tile.span === 2 ? 'aspect-[16/10] md:aspect-[16/9]' : 'aspect-[16/10] md:aspect-[4/3]'
              )}
            >
              {tile.kind === 'image' && tile.image && (
                <Image
                  src={tile.image}
                  alt={tile.image_alt ?? ''}
                  width={1600}
                  height={900}
                  sizes="(min-width: 1024px) 800px, 100vw"
                  className={cn(
                    'absolute max-w-none rounded-xl shadow-[0_20px_60px_-20px_rgba(18,6,22,0.4)] ring-1 ring-black/10 transition duration-700 group-hover:-translate-y-1',
                    tile.image_class ?? 'top-8 left-8 w-[calc(100%-4rem)]'
                  )}
                />
              )}
              {tile.kind === 'schedule' && <ScheduleMock />}
              {tile.kind === 'layout' && <LayoutMock />}
              {tile.kind === 'proof' && <ProofMock />}
              {tile.kind === 'displays' && <DisplaysMock />}
              {tile.kind === 'plan' && <PlanMock />}
            </div>
            <div className="flex flex-1 flex-col px-8 pt-7 pb-8">
              <div className="flex items-center justify-between gap-3">
                <p className="eyebrow !text-[12px]">{tile.eyebrow}</p>
                {tile.badge && (
                  <span className="bg-ink rounded-full px-2.5 py-1 text-[10px] leading-none font-semibold tracking-wide text-white uppercase">
                    {tile.badge}
                  </span>
                )}
              </div>
              <h3 className="font-rubik mt-2 text-[22px] leading-7 font-semibold tracking-tight">
                {tile.title}
              </h3>
              <p className="mt-2 text-[15px] leading-6 text-[#4b4b4b]">{tile.text}</p>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
)

/* --- CSS mocks -------------------------------------------------------------- */

const ScheduleMock = () => (
  <div className="absolute inset-0 flex items-center justify-center p-8">
    <div className="w-full max-w-[340px] rounded-2xl bg-white p-5 shadow-[0_20px_60px_-20px_rgba(18,6,22,0.35)] ring-1 ring-black/5">
      <p className="text-[11px] font-semibold tracking-wide text-[#6b6b6b] uppercase">Set the schedule</p>
      <div className="mt-3 flex gap-2">
        <span className="rounded-full bg-black/5 px-3 py-1 text-[12px] font-medium text-[#4b4b4b]">
          Always show
        </span>
        <span className="bg-primary rounded-full px-3 py-1 text-[12px] font-semibold text-white">
          Scheduled
        </span>
      </div>
      <div className="mt-4 grid grid-cols-7 gap-1.5">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d, i) => (
          <span
            key={d}
            className={cn(
              'flex h-8 items-center justify-center rounded-lg text-[12px] font-semibold',
              i < 5 ? 'bg-primary/10 text-primary' : 'bg-black/5 text-[#9a9a9a] line-through'
            )}
          >
            {d}
          </span>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between rounded-xl bg-black/[0.03] px-4 py-3">
        <div>
          <p className="text-[11px] text-[#6b6b6b]">Time of day</p>
          <p className="font-rubik text-[17px] font-semibold">11:00 – 14:00</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ffe9d6] px-3 py-1 text-[12px] font-semibold text-[#9a4b00]">
          <span className="size-1.5 rounded-full bg-[#ff8a2a]" />
          Take over
        </span>
      </div>
    </div>
  </div>
)

const LayoutMock = () => (
  <div className="absolute inset-0 flex items-center justify-center p-8">
    <div className="grid aspect-video w-full max-w-[360px] grid-cols-4 grid-rows-4 gap-1.5 rounded-xl bg-[#120616] p-1.5 shadow-[0_20px_60px_-20px_rgba(18,6,22,0.5)]">
      <div className="relative col-span-3 row-span-3 overflow-hidden rounded-md bg-gradient-to-br from-[#4a1a63] to-[#2a0f3a]">
        <span className="absolute top-2 left-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-medium text-white/80">
          Video · 1440×810
        </span>
        <IconComponent
          icon="PlayButton"
          size="medium"
          className="absolute inset-0 m-auto size-8 text-white/60"
        />
      </div>
      <div className="relative col-span-1 row-span-3 overflow-hidden rounded-md bg-[#f6f3f8] p-2">
        <span className="text-[9px] font-semibold text-[#4b4b4b]">Today</span>
        <p className="font-rubik mt-1 text-[16px] leading-none font-semibold text-[#2d033b]">7°</p>
        <div className="mt-2 space-y-1">
          {[60, 45, 70, 40].map((w) => (
            <div key={w} className="h-1 rounded bg-[#2d033b]/15" style={{ width: `${w}%` }} />
          ))}
        </div>
      </div>
      <div className="col-span-4 row-span-1 flex items-center overflow-hidden rounded-md bg-[#c147e9] px-2">
        <span className="animate-marquee whitespace-nowrap text-[10px] font-medium text-white">
          Ticker · Lunch menu changes at 11:00 · Weekly staff meeting moved to Thursday · Ticker · Lunch menu
          changes at 11:00 · Weekly staff meeting moved to Thursday ·
        </span>
      </div>
    </div>
  </div>
)

const ProofMock = () => {
  const rows = [
    ['Entrance screen', 98, '11h 52m'],
    ['Menu board left', 96, '11h 40m'],
    ['Menu board right', 91, '10h 58m'],
    ['Back office', 42, '5h 04m'],
  ] as const
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <div className="w-full max-w-[340px] rounded-2xl bg-white p-5 shadow-[0_20px_60px_-20px_rgba(18,6,22,0.35)] ring-1 ring-black/5">
        <div className="flex items-baseline justify-between">
          <p className="text-[11px] font-semibold tracking-wide text-[#6b6b6b] uppercase">Last 30 days</p>
          <p className="font-rubik text-[13px] font-semibold text-[#2d033b]">4 displays · 39h 34m</p>
        </div>
        <ul className="mt-3 space-y-2.5">
          {rows.map(([name, pct, t]) => (
            <li key={name} className="grid grid-cols-[1fr_auto] items-center gap-x-3 text-[12px]">
              <div>
                <div className="flex justify-between">
                  <span className="font-medium text-[#2f2f2f]">{name}</span>
                  <span className="text-[#6b6b6b]">{t}</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-black/5">
                  <div
                    className={cn('h-1.5 rounded-full', pct > 60 ? 'bg-[#1f9d55]' : 'bg-[#d64545]')}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/* --- Content boards: what a screen is "playing" in the mocks ------------------ */

type BoardKind = 'promo' | 'menu' | 'weather' | 'brunch' | 'video' | 'loyalty' | 'autumn' | 'beans'

const Board = ({ kind, className }: { kind: BoardKind; className?: string }) => {
  const base = cn('relative h-full w-full overflow-hidden', className)
  switch (kind) {
    case 'promo':
      return (
        <div
          className={cn(
            base,
            'bg-gradient-to-br from-[#ff7a18] via-[#e5482b] to-[#a3123a] p-[6%] text-white'
          )}
        >
          <p className="text-[clamp(8px,2.4cqw,12px)] font-semibold tracking-[0.16em] uppercase opacity-80">
            Today only
          </p>
          <p className="font-rubik mt-[2%] text-[clamp(18px,8cqw,40px)] leading-none font-bold">2 for 1</p>
          <p className="mt-[3%] text-[clamp(8px,2.8cqw,13px)] font-medium">Pastries until 11:00</p>
          <span className="absolute -right-[8%] -bottom-[18%] size-[46%] rounded-full bg-white/15" />
        </div>
      )
    case 'menu':
      return (
        <div className={cn(base, 'flex bg-[#0f2f24] p-[6%] text-white')}>
          <div className="flex-1">
            <p className="text-[clamp(7px,2.2cqw,11px)] font-semibold tracking-[0.16em] text-[#9ad4b5] uppercase">
              Lunch
            </p>
            {[
              ['Soup of the day', '9.50'],
              ['Toastie', '6.50'],
              ['Flat white', '3.90'],
            ].map(([n, p]) => (
              <div
                key={n}
                className="menu-row mt-[3%] flex justify-between gap-2 text-[clamp(7px,2.6cqw,12px)]"
              >
                <span className="truncate">{n}</span>
                <span className="opacity-80">{p}</span>
              </div>
            ))}
          </div>
          <div className="ml-[5%] w-[30%] rounded-md bg-gradient-to-br from-[#c8915a] to-[#7a4b22]" />
        </div>
      )
    case 'weather':
      return (
        <div className={cn(base, 'bg-gradient-to-br from-[#1c4e80] to-[#0b2540] p-[6%] text-white')}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[clamp(7px,2.2cqw,11px)] font-semibold tracking-[0.16em] uppercase opacity-70">
                Today
              </p>
              <p className="font-rubik text-[clamp(18px,8cqw,40px)] leading-none font-semibold">7°</p>
              <p className="mt-[2%] text-[clamp(7px,2.4cqw,11px)] opacity-80">Light rain</p>
            </div>
            <p className="font-rubik text-[clamp(10px,3.4cqw,16px)] font-semibold">11:42</p>
          </div>
          <div className="mt-[5%] flex gap-[3%]">
            {[6, 8, 9, 7, 5].map((t, i) => (
              <span
                key={`${t}-${i + 1}`}
                className="rounded bg-white/15 px-[3%] py-[1%] text-[clamp(6px,2cqw,10px)]"
              >
                {t}°
              </span>
            ))}
          </div>
        </div>
      )
    case 'brunch':
      return (
        <div className={base}>
          <Image
            src="/images/restaurant-6479818_1920-1024x717.jpg"
            alt=""
            fill
            sizes="(min-width: 768px) 300px, 25vw"
            quality={60}
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute inset-x-[6%] bottom-[8%] text-white">
            <p className="font-rubik text-[clamp(10px,4cqw,18px)] leading-none font-semibold">
              Sunday brunch
            </p>
            <p className="mt-[1%] text-[clamp(7px,2.4cqw,11px)] opacity-85">10:00 – 14:00 · book a table</p>
          </div>
        </div>
      )
    case 'video':
      return (
        <div className={cn(base, 'bg-gradient-to-br from-[#2a0f3a] to-[#0f0a14]')}>
          <span className="absolute inset-0 m-auto flex size-[22%] items-center justify-center rounded-full bg-white/15 ring-1 ring-white/40">
            <span className="ml-[10%] size-0 border-y-[0.5em] border-l-[0.8em] border-y-transparent border-l-white text-[clamp(5px,2cqw,9px)]" />
          </span>
          <span className="absolute right-[5%] bottom-[6%] rounded bg-black/50 px-[3%] py-[1%] font-mono text-[clamp(6px,2cqw,10px)] text-white">
            0:45
          </span>
        </div>
      )
    case 'loyalty':
      return (
        <div className={cn(base, 'bg-[#1f6b4a] p-[6%] text-white')}>
          <p className="text-[clamp(7px,2.4cqw,11px)] font-semibold tracking-[0.16em] uppercase opacity-80">
            Loyalty card
          </p>
          <p className="font-rubik mt-[2%] text-[clamp(9px,3.2cqw,15px)] leading-tight font-semibold">
            Every 10th coffee is free
          </p>
          <div className="mt-[6%] flex gap-[3%]">
            {Array.from({ length: 6 }).map((_, i) => (
              <span
                key={`stamp-${i + 1}`}
                className={cn('aspect-square w-[12%] rounded-full', i < 4 ? 'bg-white' : 'bg-white/25')}
              />
            ))}
          </div>
        </div>
      )
    case 'autumn':
      return (
        <div className={cn(base, 'bg-gradient-to-br from-[#8a3b12] to-[#4a1d0a] p-[6%] text-white')}>
          <p className="text-[clamp(7px,2.4cqw,11px)] font-semibold tracking-[0.16em] uppercase opacity-80">
            New this season
          </p>
          <p className="font-rubik mt-[2%] text-[clamp(10px,3.6cqw,17px)] leading-tight font-semibold">
            Pumpkin spice latte
          </p>
          <span className="absolute -right-[6%] -bottom-[20%] size-[50%] rotate-12 rounded-[40%_60%_45%_55%] bg-[#e8a33d]/60" />
        </div>
      )
    case 'beans':
      return (
        <div className={cn(base, 'bg-[#1b120e] p-[6%] text-white')}>
          <p className="text-[clamp(7px,2.4cqw,11px)] font-semibold tracking-[0.16em] text-[#d3a873] uppercase">
            Single origin
          </p>
          <p className="font-rubik mt-[2%] text-[clamp(9px,3.4cqw,16px)] leading-tight font-semibold">
            Ethiopia Yirgacheffe
          </p>
          <div className="mt-[5%] flex gap-[2%]">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={`bean-${i + 1}`}
                className="h-[0.9em] w-[0.6em] rotate-[-25deg] rounded-full bg-[#6b4a2b] text-[clamp(6px,2.4cqw,11px)]"
              />
            ))}
          </div>
        </div>
      )
  }
}

const StatusPill = ({ online }: { online: boolean }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] leading-4 font-semibold',
      online ? 'bg-[#e3f5ea] text-[#1f7a45]' : 'bg-[#fbe3e3] text-[#b42323]'
    )}
  >
    <span className={cn('size-1.5 rounded-full', online ? 'bg-[#1f9d55]' : 'bg-[#d64545]')} />
    {online ? 'Online' : 'Offline'}
  </span>
)

/** The Displays page as the portal draws it, with content on every screen. */
const DisplaysMock = () => {
  const cards: { name: string; where: string; board: BoardKind; online: boolean; tag?: string }[] = [
    { name: 'Entrance screen', where: 'Downtown', board: 'promo', online: true, tag: 'Promo' },
    { name: 'Menu board left', where: 'Downtown', board: 'menu', online: true, tag: 'Menu' },
    { name: 'Window display', where: 'Harbour', board: 'brunch', online: true },
    { name: 'Lobby', where: 'Airport', board: 'video', online: true },
    { name: 'Staff room', where: 'Harbour', board: 'weather', online: true },
    { name: 'Back office', where: 'Airport', board: 'loyalty', online: false },
  ]
  return (
    <div className="absolute inset-0 p-6 md:p-7">
      <div className="grid h-full grid-cols-3 gap-3">
        {cards.map((c) => (
          <div
            key={c.name}
            className="flex flex-col overflow-hidden rounded-xl bg-white shadow-[0_10px_30px_-18px_rgba(18,6,22,0.4)] ring-1 ring-black/5"
          >
            <div className="relative aspect-[16/9] @container">
              <Board kind={c.board} className={cn('pt-[18%]', !c.online && 'opacity-60 grayscale-[35%]')} />
              <div className="absolute top-2 left-2">
                <StatusPill online={c.online} />
              </div>
              <span className="absolute top-2 right-2 size-3.5 rounded-[4px] bg-white/90 ring-1 ring-black/10" />
              {c.tag && (
                <span className="absolute right-2 bottom-2 rounded-full bg-white/90 px-2 py-0.5 font-mono text-[9px] leading-4 font-semibold text-[#2d033b]">
                  {c.tag}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-[11px] leading-4 font-semibold text-[#120616]">{c.name}</p>
                <p className="truncate text-[9px] leading-3 text-[#6b6b6b]">{c.where}</p>
              </div>
              <span className="text-[#9a9a9a]">···</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** A channel's scheduled plan with varied content and its publish state. */
const PlanMock = () => {
  const rows: {
    name: string
    type: string
    board: BoardKind
    dur: string
    when?: string
    nested?: boolean
    takeover?: boolean
  }[] = [
    { name: 'Seasonal promos', type: 'Playlist', board: 'autumn', dur: '0:30' },
    { name: 'Pumpkin spice latte', type: 'Image', board: 'autumn', dur: '0:12', nested: true },
    { name: 'Loyalty card', type: 'Image', board: 'loyalty', dur: '0:08', nested: true },
    { name: 'Sunday brunch', type: 'Image', board: 'brunch', dur: '0:10', when: 'Sat–Sun · 10:00–14:00' },
    { name: 'Brand film', type: 'Video', board: 'video', dur: '0:45' },
    {
      name: 'Lunch menu',
      type: 'Image',
      board: 'menu',
      dur: '0:12',
      when: 'Mon–Fri · 11:00–14:00',
      takeover: true,
    },
    { name: 'Weather & time', type: 'Widget', board: 'weather', dur: '0:08' },
  ]
  const typeTone: Record<string, string> = {
    Playlist: 'bg-[#fff1dc] text-[#9a4b00]',
    Image: 'bg-[#e3f5ea] text-[#1f7a45]',
    Video: 'bg-[#e8e3ff] text-[#4b2ba3]',
    Widget: 'bg-[#e3eefb] text-[#1d4f91]',
  }
  return (
    <div className="absolute inset-0 p-6 md:p-7">
      <div className="flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-[0_20px_50px_-24px_rgba(18,6,22,0.45)] ring-1 ring-black/5">
        <div className="flex items-center justify-between border-b border-black/5 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <p className="text-[12px] font-semibold text-[#120616]">Scheduled plan</p>
            <span className="rounded-full bg-[#fff1dc] px-2 py-0.5 text-[9px] leading-4 font-semibold text-[#9a4b00]">
              Unpublished
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-black/5 px-2.5 py-1 text-[10px] leading-4 font-semibold text-[#2d033b]">
              + Add to plan
            </span>
            <span className="rounded-full bg-[#810ca8] px-2.5 py-1 text-[10px] leading-4 font-semibold text-white">
              Publish changes
            </span>
          </div>
        </div>
        <ul className="flex-1 divide-y divide-black/5 overflow-hidden">
          {rows.map((r) => (
            <li
              key={r.name}
              className={cn('flex items-center gap-3 px-4 py-1', r.nested && 'bg-[#faf8fb] pl-9')}
            >
              <div
                className={cn(
                  'relative shrink-0 overflow-hidden rounded-md @container [&_.menu-row]:hidden [&_p]:hidden',
                  r.nested ? 'h-9 w-16' : 'h-11 w-20'
                )}
              >
                <Board kind={r.board} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] leading-4 font-semibold text-[#120616]">{r.name}</p>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-px text-[8px] leading-3 font-semibold',
                      typeTone[r.type]
                    )}
                  >
                    {r.type}
                  </span>
                  {r.takeover && (
                    <span className="rounded-full bg-[#ffe9d6] px-1.5 py-px text-[8px] leading-3 font-semibold text-[#9a4b00]">
                      Take over
                    </span>
                  )}
                </div>
              </div>
              {r.when && (
                <p className="hidden shrink-0 font-mono text-[9px] text-[#6b6b6b] lg:block">{r.when}</p>
              )}
              <p className="w-8 shrink-0 text-right font-mono text-[10px] font-semibold text-[#120616]">
                {r.dur}
              </p>
              {!r.nested && (
                <div className="flex shrink-0 gap-1">
                  <span className="size-5 rounded-md ring-1 ring-black/10" />
                  <span className="size-5 rounded-md ring-1 ring-[#d64545]/40" />
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default Bento
